import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './styles/sport-progress-theme.css';
import { applyCssVariables } from './theme/designSystem';
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import DemoLoginPage from './pages/DemoLoginPage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import InviteConfirmPage from './pages/InviteConfirmPage';
import ProfileSelectPage from './pages/ProfileSelectPage';
import ChangeAccountTypePage from './pages/ChangeAccountTypePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AdminEmailSettingsPage from './pages/AdminEmailSettingsPage';
import RegisterPage from './pages/RegisterPage';
import ParentLinkChildPage from './pages/ParentLinkChildPage';
import UploadPage from './pages/UploadPage';
import VideoPage from './pages/VideoPage';
import SchoolDashboard from './components/SchoolDashboard';
import StudentList from './components/StudentList';
import AttendanceCalendar from './components/AttendanceCalendar';
import SubscriptionManager from './components/SubscriptionManager';
import ScheduleManager from './components/ScheduleManager';
import EventManager from './components/EventManager';
import StaffManager from './components/StaffManager';
import ReportsPage from './pages/ReportsPage';
import MyBalancePage from './pages/MyBalancePage';
import ApplyPage from './pages/ApplyPage';
import FindSchoolPage from './pages/FindSchoolPage';
import ApplicationsPage from './pages/ApplicationsPage';
import PlatformBillingPage from './pages/PlatformBillingPage';
import BillingPlansPage from './pages/BillingPlansPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import SchoolPaymentSuccessPage from './pages/SchoolPaymentSuccessPage';
import SchoolSettingsPage from './pages/SchoolSettingsPage';
import SuperAdminSchoolBillingPage from './pages/SuperAdminSchoolBillingPage';
import AdminIntegrationsPage from './pages/AdminIntegrationsPage';
import AdminSmsBillingPage from './pages/AdminSmsBillingPage';
import SmsManagementPage from './pages/admin/SmsManagementPage';
import AdminPersonMergePage from './pages/AdminPersonMergePage';
import AdminPosCatalogPage from './pages/AdminPosCatalogPage';
import AdminReferencesPage from './pages/AdminReferencesPage';
import AdminExportTemplatesPage from './pages/AdminExportTemplatesPage';
import SchoolIntegrationsPage from './pages/SchoolIntegrationsPage';
import SelectSchoolPage from './pages/SelectSchoolPage';
import SchoolSessionGate from './components/SchoolSessionGate';
import LegalDocumentPublicPage from './pages/LegalDocumentPublicPage';
import AboutPage from './pages/AboutPage';
import SiteMeta from './components/SiteMeta';
import SchoolOrganizationSettingsPage from './pages/SchoolOrganizationSettingsPage';
import LegalDocumentsAdminPage from './pages/admin/LegalDocumentsAdminPage';
import PlatformSettingsPage from './pages/admin/PlatformSettingsPage';
import I18nManagerPage from './pages/admin/I18nManagerPage';
import BugReportsPage from './pages/admin/BugReportsPage';
import TestersPage from './pages/admin/TestersPage';
import RemoteConfigPage from './pages/admin/RemoteConfigPage';
import NotificationQueuePage from './pages/admin/NotificationQueuePage';
import ActivityLogPage from './pages/admin/ActivityLogPage';
import BugReportWidget from './components/BugReportWidget';
import BetaOfferPopup from './components/BetaOfferPopup';
import ProfilePage from './pages/ProfilePage';
import SportProfileEditPage from './pages/profile/SportProfileEditPage';
import MarketplaceSettingsPage from './pages/profile/MarketplaceSettingsPage';
import MarketplacePage from './pages/marketplace/MarketplacePage';
import PublicProfilePage from './pages/marketplace/PublicProfilePage';
import PublicSchoolPage from './pages/marketplace/PublicSchoolPage';
import MarketplaceAdminPage from './pages/admin/MarketplaceAdminPage';
import InventoryManagePage from './pages/InventoryManagePage';
import RentalPage from './pages/RentalPage';
import SchoolVenuesPage from './pages/SchoolVenuesPage';
import VenueEquipmentPage from './pages/VenueEquipmentPage';
import CashierPage from './pages/CashierPage';
import NomenclaturePage from './pages/NomenclaturePage';
import LoyaltyPage from './pages/LoyaltyPage';
import { getEffectiveNavRole } from './utils/roles';
import { OnboardingProvider } from './onboarding/OnboardingProvider';
import LocaleRefreshBoundary from './components/LocaleRefreshBoundary';
import LanguageDetectionGate from './components/LanguageDetectionGate';

applyCssVariables();

function PrivateRoute({ children }) {
    const token = localStorage.getItem('token');
    if (!token) {
        return <Navigate to="/login" replace />;
    }
    return <SchoolSessionGate>{children}</SchoolSessionGate>;
}

function RoleRoute({ children, allowedRoles, fallback = '/my-balance' }) {
    const token = localStorage.getItem('token');
    if (!token) return <Navigate to="/login" replace />;
    const role = getEffectiveNavRole();
    if (!allowedRoles.includes(role)) {
        return <Navigate to={fallback} replace />;
    }
    return children;
}

function App() {
    return (
        <BrowserRouter>
            <SiteMeta />
            <LocaleRefreshBoundary>
            <LanguageDetectionGate>
            <OnboardingProvider>
                <Routes>
                {/* Старые маршруты */}
                <Route path="/" element={<HomePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/demo-login" element={<DemoLoginPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/invite/confirm/:token" element={<PrivateRoute><InviteConfirmPage /></PrivateRoute>} />
                <Route path="/select-profile" element={<ProfileSelectPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/change-password" element={<ChangePasswordPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/parent/link-child" element={<PrivateRoute><ParentLinkChildPage /></PrivateRoute>} />
                <Route path="/privacy-policy" element={<LegalDocumentPublicPage docKey="privacy" />} />
                <Route path="/privacy" element={<LegalDocumentPublicPage docKey="privacy" />} />
                <Route path="/terms" element={<LegalDocumentPublicPage docKey="terms" />} />
                <Route path="/terms-of-service" element={<LegalDocumentPublicPage docKey="terms" />} />
                <Route path="/offer" element={<LegalDocumentPublicPage docKey="offer" />} />
                <Route path="/cookies" element={<LegalDocumentPublicPage docKey="cookies" />} />
                <Route path="/refund" element={<LegalDocumentPublicPage docKey="refund" />} />
                <Route path="/refund-policy" element={<LegalDocumentPublicPage docKey="refund" />} />
                <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                <Route path="/profile/sport" element={<PrivateRoute><SportProfileEditPage /></PrivateRoute>} />
                <Route path="/profile/marketplace-settings" element={<PrivateRoute><MarketplaceSettingsPage /></PrivateRoute>} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/p/:slug" element={<PublicProfilePage />} />
                <Route path="/school/:slug" element={<PublicSchoolPage />} />
                <Route path="/account-type" element={<PrivateRoute><ChangeAccountTypePage /></PrivateRoute>} />
                <Route path="/select-school" element={<PrivateRoute><SelectSchoolPage /></PrivateRoute>} />
                <Route path="/video/:id" element={<VideoPage />} />
                <Route path="/upload" element={<PrivateRoute><UploadPage /></PrivateRoute>} />
                <Route path="/apply" element={<PrivateRoute><ApplyPage /></PrivateRoute>} />
                <Route path="/coaches/find-school" element={<PrivateRoute><FindSchoolPage /></PrivateRoute>} />
                <Route path="/applications" element={<PrivateRoute><ApplicationsPage /></PrivateRoute>} />
                <Route path="/my-balance" element={<PrivateRoute><MyBalancePage /></PrivateRoute>} />
                <Route path="/rental" element={<PrivateRoute><RentalPage /></PrivateRoute>} />
                <Route path="/school/inventory" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner']} fallback="/school">
                            <InventoryManagePage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/payment/school-success" element={<PrivateRoute><SchoolPaymentSuccessPage /></PrivateRoute>} />
                {/* Новые маршруты школы */}
                <Route path="/school" element={<PrivateRoute><SchoolDashboard /></PrivateRoute>} />
                <Route path="/students" element={<PrivateRoute><StudentList /></PrivateRoute>} />
                <Route path="/schedule" element={<PrivateRoute><ScheduleManager /></PrivateRoute>} />
                <Route path="/school/venues" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/schedule">
                            <SchoolVenuesPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/school/venues/:id" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/schedule">
                            <VenueEquipmentPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/venue/:id/equipment" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/schedule">
                            <VenueEquipmentPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/cashier" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin', 'Coach']} fallback="/schedule">
                            <CashierPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/nomenclature" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/school">
                            <NomenclaturePage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/loyalty" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/school">
                            <LoyaltyPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/staff" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/students">
                            <StaffManager />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/events" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']} fallback="/schedule">
                            <EventManager />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/attendance" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Coach', 'Admin']} fallback="/schedule">
                            <AttendanceCalendar />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/subscriptions" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']}>
                            <SubscriptionManager />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
                <Route path="/billing/plans" element={<BillingPlansPage />} />
                <Route path="/billing" element={<PrivateRoute><PlatformBillingPage /></PrivateRoute>} />
                <Route path="/school/settings" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner']}>
                            <SchoolSettingsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/school/organization-settings" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner', 'Admin']}>
                            <SchoolOrganizationSettingsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/billing/success" element={<PrivateRoute><BillingSuccessPage /></PrivateRoute>} />
                <Route path="/admin/platform-settings" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <PlatformSettingsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/school-billing" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <SuperAdminSchoolBillingPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/integrations" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminIntegrationsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/email-settings" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminEmailSettingsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/notifications/queue" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <NotificationQueuePage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/activity-log" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <ActivityLogPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/sms-billing" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminSmsBillingPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/sms" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <SmsManagementPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/person-merge" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminPersonMergePage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/pos-catalog" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminPosCatalogPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/references" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminReferencesPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/legal-documents" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <LegalDocumentsAdminPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/export-templates" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <AdminExportTemplatesPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/i18n" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <I18nManagerPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/bugs" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <BugReportsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/testers" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <TestersPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/config" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <RemoteConfigPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/marketplace" element={<Navigate to="/admin/marketplace/products" replace />} />
                <Route path="/admin/marketplace/products" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <MarketplaceAdminPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/marketplace/purchases" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <MarketplaceAdminPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/marketplace/verification" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <MarketplaceAdminPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/admin/marketplace/analytics" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Admin']}>
                            <MarketplaceAdminPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
                <Route path="/school/settings/integrations" element={
                    <PrivateRoute>
                        <RoleRoute allowedRoles={['Owner']}>
                            <SchoolIntegrationsPage />
                        </RoleRoute>
                    </PrivateRoute>
                } />
            </Routes>
            <BetaOfferPopup />
            <BugReportWidget />
            </OnboardingProvider>
            </LanguageDetectionGate>
            </LocaleRefreshBoundary>
        </BrowserRouter>
    );
}

export default App;